import { ChangeDetectorRef, Component, inject, PLATFORM_ID, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthService } from '../../../../core/services/auth.service';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SuccessError } from '../../../general/components/success-error/success-error';
import { FormsModule } from '@angular/forms';
import { LoaderComponent } from '../../../general/components/loader/loader.component';
import { HapticsService } from '../../../general/services/haptics.service';
import { GtmService } from '../../../../core/services/gtm.service';
import { SeoService } from '../../../services/seo.service';
import { environment } from '../../../../../environments/environment';

type ViewState = 'initial' | 'loading' | 'verify-success' | 'verify-failed' | 'resend-success' | 'resend-failed';

@Component({
  selector: 'app-verify-user',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    RouterModule,
    SuccessError,
    FormsModule,
    LoaderComponent
  ],
  templateUrl: './verify-user.html',
  styleUrl: './verify-user.scss',
})
export class VerifyUserComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);
  private gtm = inject(GtmService);
  private haptics = inject(HapticsService);
  private authService = inject(AuthService);
  private router = inject(Router);

  @Input() isDialogMode = false;
  @Input() overrideEmail: string | null = null;
  @Output() verifyComplete = new EventEmitter<void>();
  @Output() goToLogin = new EventEmitter<void>();
  
  currentView: ViewState = 'initial';
  
  email: string | null = null;
  userEmail: string | null = null;
  verificationCode: string = '';
  errorMessage: string | null = null;

  private seo = inject(SeoService);
  platformId = inject(PLATFORM_ID);

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.gtm.pageView({
        page_path: '/login/verify',
        page_title: 'Verify User | Horizinns'
      });

      if (this.overrideEmail) {
        this.email = this.overrideEmail;
        this.userEmail = this.overrideEmail;
      } else {
        this.route.queryParams.subscribe(params => {
          this.email = params['email'] || null;
          this.userEmail = this.email;
        });
      }
    }
    
    this.seo.updateMetaTags('Verify User', 'Verify User for Horizinns', undefined, `${environment.appUrl}/login/verify`);
  }

  resendVerification() {
    const emailToUse = this.userEmail || this.email;
  
    if (!emailToUse || !emailToUse.includes('@')) {
      this.errorMessage = 'Please enter a valid email address.';
      return;
    }

    this.haptics.click();
    this.currentView = 'loading';
    this.errorMessage = null;

    this.authService.resendVerification(emailToUse).subscribe({
      next: () => {
        setTimeout(() => {
          this.currentView = 'resend-success';
          this.verificationCode = ''; // Clear the input field for the new code
          this.cdr.detectChanges();
        });
      },
      error: (error) => {
        setTimeout(() => {
          this.errorMessage = error.message; 
          this.currentView = 'resend-failed';
          this.cdr.markForCheck();
        });
      }
    });
  }

  verifyEmail() {
    if (!this.userEmail || !this.verificationCode) {
      this.errorMessage = 'Email and verification code are required.';
      this.currentView = 'verify-failed';
      return;
    }

    this.haptics.click();
    this.currentView = 'loading';
    this.errorMessage = null;

    // Ensure the code is uppercase before sending to the backend
    const payload = {
      email: this.userEmail,
      code: this.verificationCode.toUpperCase()
    };

    // NOTE: You will need to update your AuthService to accept this object 
    // instead of just a single token string.
    this.authService.verifyToken(payload).subscribe({
      next: (verified) => {
        setTimeout(() => {
          if (verified) {
            this.currentView = 'verify-success';
            this.cdr.markForCheck();
          } else {
            this.errorMessage = 'Verification failed. The code may be invalid or expired.';
            this.currentView = 'verify-failed';
            this.cdr.markForCheck();
          }
          this.cdr.detectChanges(); 
        });
      },
      error: (error) => {
        setTimeout(() => {
          this.errorMessage = error.message || 'An error occurred during verification.';
          this.currentView = 'verify-failed';
          this.cdr.detectChanges();
        });
      }
    });
  }

  handleLoginRedirect() {
    if (this.isDialogMode) {
      this.goToLogin.emit();
    } else {
      // Inject router at the top of the file if you haven't already: private router = inject(Router);
      this.router.navigate(['/login']);
    }
  }
}